import React, { useState } from 'react';
import { Trash2, Send, Mail, User, Phone, MessageSquare, Loader2, CheckCircle, Info } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import ColorSwatchPicker from './ColorSwatchPicker';

interface ContactFormBlockData {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonColor: string;
  fields: {
    name: boolean;
    email: boolean;
    phone: boolean;
    message: boolean;
  };
}

interface ContactFormBlockProps {
  data: ContactFormBlockData;
  onChange: (data: ContactFormBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
  salePageId?: string;
}

const ContactFormBlock: React.FC<ContactFormBlockProps> = ({ data, onChange, onDelete, isPreview, salePageId }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!salePageId) {
      alert('ไม่สามารถส่งข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const sendContactFormFn = httpsCallable(functions, 'sendContactForm');
      const result = await sendContactFormFn({
        salePageId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      }) as { data: { success: boolean; message: string } };

      if (result.data.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      setSubmitError(error.message || 'เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isPreview) {
    return (
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-4">
            <h3 className="text-xl font-bold text-gray-900 mb-1">{data.title || 'ติดต่อเรา'}</h3>
            {data.subtitle && <p className="text-sm text-gray-600">{data.subtitle}</p>}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {data.fields.name && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <User className="w-3 h-3 inline mr-1" />
                  ชื่อ-นามสกุล
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="กรอกชื่อของคุณ"
                  required
                />
              </div>
            )}

            {data.fields.email && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <Mail className="w-3 h-3 inline mr-1" />
                  อีเมล
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>
            )}

            {data.fields.phone && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <Phone className="w-3 h-3 inline mr-1" />
                  เบอร์โทรศัพท์
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="08X-XXX-XXXX"
                  required
                />
              </div>
            )}

            {data.fields.message && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <MessageSquare className="w-3 h-3 inline mr-1" />
                  ข้อความ
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="พิมพ์ข้อความของคุณ..."
                  required
                />
              </div>
            )}

            {submitSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-700 text-sm">
                <CheckCircle className="w-5 h-5" />
                <span>ส่งข้อความเรียบร้อยแล้ว! เจ้าของหน้าจะติดต่อกลับโดยเร็ว</span>
              </div>
            )}

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 rounded-lg font-semibold text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: data.buttonColor }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  กำลังส่ง...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {data.buttonText || 'ส่งข้อความ'}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">แบบฟอร์มติดต่อ</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">รับข้อความจากลูกค้า</p>
              <p className="text-gray-300">สร้างฟอร์มให้ลูกค้าติดต่อสอบถาม ข้อความจะส่งตรงไปยัง Dashboard ของคุณ</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">หัวข้อ</label>
          <input
            type="text"
            value={data.title}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="ติดต่อเรา"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">คำอธิบาย</label>
          <input
            type="text"
            value={data.subtitle}
            onChange={(e) => onChange({ ...data, subtitle: e.target.value })}
            placeholder="กรอกข้อมูลเพื่อติดต่อเรา"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ฟิลด์ที่ต้องการ</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.fields.name}
                onChange={(e) => onChange({ ...data, fields: { ...data.fields, name: e.target.checked } })}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-sm">ชื่อ-นามสกุล</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.fields.email}
                onChange={(e) => onChange({ ...data, fields: { ...data.fields, email: e.target.checked } })}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-sm">อีเมล</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.fields.phone}
                onChange={(e) => onChange({ ...data, fields: { ...data.fields, phone: e.target.checked } })}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-sm">เบอร์โทรศัพท์</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.fields.message}
                onChange={(e) => onChange({ ...data, fields: { ...data.fields, message: e.target.checked } })}
                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-sm">ข้อความ</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">ข้อความบนปุ่ม</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={data.buttonColor}
              onChange={(hex) => onChange({ ...data, buttonColor: hex })}
            />
            <input
              type="text"
              value={data.buttonText}
              onChange={(e) => onChange({ ...data, buttonText: e.target.value })}
              placeholder="ส่งข้อความ"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFormBlock;
